import { useState, useEffect, useMemo } from 'react';
import { format, addMilliseconds } from 'date-fns';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EnhancedResourceSelect } from '@/components/booking/EnhancedResourceSelect';
import { CalendarIcon, Loader2, AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Booking, Resource, WorkingHoursProfile } from '@/types';
import { useScheduler } from '@/contexts/SchedulerContext';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';

interface MoveBookingDialogProps {
  open: boolean;
  onClose: () => void;
  booking: Booking | null;
  resources: Resource[];
  onMove: (bookingId: string | number, newDate: Date, newResourceId: string) => void;
  calculateUtilization?: (resourceId: string) => number;
}

export function MoveBookingDialog({
  open,
  onClose,
  booking,
  resources,
  onMove,
  calculateUtilization,
}: MoveBookingDialogProps) {
  const { settings } = useScheduler();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedResourceId, setSelectedResourceId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);

  // Initialize form values when booking changes
  useEffect(() => {
    if (booking && open) {
      // Set current booking date - handle both startTime and startDate for compatibility
      try {
        const bookingDate = new Date(booking.startTime || (booking as any).startDate);
        if (!isNaN(bookingDate.getTime())) {
          setSelectedDate(bookingDate);
        }
      } catch (error) {
        console.error('Error parsing booking date:', error);
      }
      
      // Set current resource
      setSelectedResourceId(booking.resourceId ? String(booking.resourceId) : '');
    }
  }, [booking, open]);

  // Reset form when dialog closes
  useEffect(() => {
    if (!open) {
      setSelectedDate(undefined);
      setSelectedResourceId('');
      setIsSubmitting(false);
      setIsDatePickerOpen(false);
    }
  }, [open]);

  // Handle date selection with automatic dismissal
  const handleDateSelect = (date: Date | undefined) => {
    setSelectedDate(date);
    // Auto-close the date picker after selection
    if (date) {
      setTimeout(() => {
        setIsDatePickerOpen(false);
      }, 200);
    }
  };

  const handleMove = async () => {
    if (!booking || !selectedDate || !selectedResourceId) return;

    // Validate that the selected date is a working day
    if (!isWorkingDay(selectedDate)) {
      toast.error('Cannot move booking to a non-working day', {
        description: 'Please select a date that falls on a working day for this resource.',
      });
      return;
    }

    // Calculate the booking's end date after moving
    const originalStartTime = new Date(booking.startTime);
    const originalEndTime = new Date(booking.endTime);
    const bookingDuration = originalEndTime.getTime() - originalStartTime.getTime();
    
    // Calculate new end date by adding duration to selected date
    const newStartDate = new Date(selectedDate);
    newStartDate.setHours(originalStartTime.getHours(), originalStartTime.getMinutes(), 0, 0);
    const newEndDate = addMilliseconds(newStartDate, bookingDuration);
    
    // Check if end date falls on a working day
    if (!isWorkingDay(newEndDate)) {
      const resourceName = selectedResource?.name || 'this resource';
      const workingDays = getWorkingDaysDisplay();
      const endDayName = getDayName(newEndDate);
      
      toast.error('Cannot move booking to this date', {
        description: `This booking would extend to ${endDayName}, which is outside ${resourceName}'s work schedule (${workingDays})`,
        duration: 5000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await onMove(booking.id, selectedDate, selectedResourceId);
      onClose();
    } catch (error) {
      console.error('Error moving booking:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectedResource = resources.find(r => String(r.id) === selectedResourceId);
  
  // Get the working profile for the selected resource
  const workingProfile = useMemo((): WorkingHoursProfile => {
    if (selectedResource?.useCustomWorkingProfile && selectedResource.customWorkingProfile) {
      return selectedResource.customWorkingProfile;
    }
    return settings.globalWorkingProfile;
  }, [selectedResource, settings.globalWorkingProfile]);

  // Function to check if a date is a working day
  const isWorkingDay = (date: Date): boolean => {
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const dayName = dayNames[dayOfWeek] as keyof WorkingHoursProfile;
    return workingProfile[dayName] > 0;
  };

  // Helper to get day name from date
  const getDayName = (date: Date): string => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return dayNames[date.getDay()];
  };

  // Helper to get working days list for display
  const getWorkingDaysDisplay = (): string => {
    const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
    const dayKeys: (keyof WorkingHoursProfile)[] = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    return dayNames
      .filter((_, index) => workingProfile[dayKeys[index]] > 0)
      .join(', ');
  };

  // Check if moving to this date would cause booking to extend beyond working days
  const wouldExtendBeyondWorkingDays = (date: Date): boolean => {
    if (!booking) return false;
    
    try {
      const originalStartTime = new Date(booking.startTime);
      const originalEndTime = new Date(booking.endTime);
      const bookingDuration = originalEndTime.getTime() - originalStartTime.getTime();
      
      const newStartDate = new Date(date);
      newStartDate.setHours(originalStartTime.getHours(), originalStartTime.getMinutes(), 0, 0);
      const newEndDate = addMilliseconds(newStartDate, bookingDuration);
      
      return !isWorkingDay(newEndDate);
    } catch {
      return false;
    }
  };

  // Function to disable non-working days in the calendar
  const disabledDates = (date: Date): boolean => {
    // Disable if the start date is not a working day
    if (!isWorkingDay(date)) return true;
    
    // Disable if the booking would extend beyond working days
    if (wouldExtendBeyondWorkingDays(date)) return true;
    
    return false;
  };
  
  // Check if there are any changes from the original booking
  const hasChanges = booking && (() => {
    try {
      const originalDate = new Date(booking.startTime || (booking as any).startDate);
      const dateChanged = selectedDate && !isNaN(originalDate.getTime()) && 
        selectedDate.toDateString() !== originalDate.toDateString();
      const resourceChanged = selectedResourceId !== String(booking.resourceId);
      return dateChanged || resourceChanged;
    } catch {
      return false;
    }
  })();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Move Booking</DialogTitle>
          <DialogDescription>
            Change the date and/or resource for this booking.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Date Picker */}
          <div className="space-y-2">
            <Label htmlFor="date">Date</Label>
            <Popover open={isDatePickerOpen} onOpenChange={setIsDatePickerOpen}>
              <PopoverTrigger asChild>
                <Button
                  id="date"
                  variant="outline"
                  className={cn(
                    'w-full justify-start text-left font-normal',
                    !selectedDate && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {selectedDate ? format(selectedDate, 'PPP') : 'Select date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDates}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
            {selectedResource && (
              <Alert className="mt-2">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <div>Non-working days are disabled based on {selectedResource.useCustomWorkingProfile ? 'custom' : 'global'} working profile</div>
                  <div className="mt-1 text-muted-foreground">
                    Working days: {Object.entries(workingProfile)
                      .filter(([_, hours]) => hours > 0)
                      .map(([day]) => day.charAt(0).toUpperCase() + day.slice(1, 3))
                      .join(', ')}
                  </div>
                </AlertDescription>
              </Alert>
            )}
          </div>

          {/* Resource Selector */}
          <div className="space-y-2">
            <Label htmlFor="resource">Resource</Label>
            <EnhancedResourceSelect
              resources={resources}
              selectedResourceId={selectedResourceId}
              onSelect={setSelectedResourceId}
              calculateUtilization={calculateUtilization}
              placeholder="Select resource..."
            />
          </div>

          {/* Current vs New Summary */}
          {booking && hasChanges && (
            <div className="rounded-lg border bg-muted/50 p-4 space-y-2 text-sm">
              <div className="font-medium">Changes:</div>
              {(() => {
                try {
                  const originalDate = new Date(booking.startTime || (booking as any).startDate);
                  const dateChanged = selectedDate && !isNaN(originalDate.getTime()) && 
                    selectedDate.toDateString() !== originalDate.toDateString();
                  
                  return (
                    <>
                      {dateChanged && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Date:</span>
                          <span>
                            {format(originalDate, 'PP')} → {format(selectedDate, 'PP')}
                          </span>
                        </div>
                      )}
                      {selectedResourceId !== String(booking.resourceId) && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Resource:</span>
                          <span>
                            {resources.find(r => String(r.id) === String(booking.resourceId))?.name || 'Unknown'} → {selectedResource?.name || 'Unknown'}
                          </span>
                        </div>
                      )}
                    </>
                  );
                } catch {
                  return null;
                }
              })()}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleMove}
            disabled={!selectedDate || !selectedResourceId || !hasChanges || isSubmitting}
          >
            {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Move Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
